// Wires the multi-file course-attachments form. Per picked file:
//   1. Render a row with the filename and an editable title input
//      (defaulted to the filename minus extension).
//   2. Sign + PUT each file on pick so titles edit in parallel;
//      submit enables once every upload is ready.
//   3. On submit, POST CreateCourseDocument once per row with that
//      row's title + storage_key, then reload the page so the
//      attachments card reflects the new state.

import { onceGuard } from "./bind_once.js";
import { request, signAndPut } from "./presigned_upload.js";

const firstBind = onceGuard();

export function initAcademyAttachmentsForm(root = document) {
  const forms = root.querySelectorAll("[data-academy-attachments-form]");
  forms.forEach((form) => {
    if (!firstBind(form)) return;
    wireForm(form);
  });
}

function wireForm(form) {
  const signEndpoint = form.getAttribute("data-sign-endpoint");
  const createEndpoint = form.getAttribute("data-create-endpoint");
  const input = form.querySelector("[data-academy-attachments-input]");
  const dropzone = form.querySelector("[data-academy-attachments-dropzone]");
  const list = form.querySelector("[data-academy-attachments-rows]");
  const status = form.querySelector("[data-academy-attachments-status]");
  const submit = form.querySelector("[data-academy-attachments-submit]");
  if (!input || !dropzone || !list || !status || !submit) return;

  /** @type {Row[]} */
  const rows = [];
  let submitting = false;

  const setStatus = (text) => {
    status.textContent = text;
    status.classList.toggle("hidden", !text);
  };

  const refreshSubmit = () => {
    if (rows.length === 0 || submitting) {
      submit.disabled = true;
      return;
    }
    submit.disabled = rows.some((r) => !r.ready);
  };

  // The rows hold still while they attach: a pick or removal mid-submit
  // would change what the loop sends.
  const removeRow = (row) => {
    if (submitting) return;
    const i = rows.indexOf(row);
    if (i === -1) return;
    rows.splice(i, 1);
    row.el.remove();
    if (rows.length === 0) list.classList.add("hidden");
    refreshSubmit();
  };

  const handleFiles = (files) => {
    if (!files || files.length === 0) return;
    if (submitting) {
      setStatus("Wait for these files to attach, then add more.");
      input.value = "";
      return;
    }
    setStatus("");
    Array.from(files).forEach((file) => {
      const row = createRow(file, () => removeRow(row));
      rows.push(row);
      list.appendChild(row.el);
      list.classList.remove("hidden");
      refreshSubmit();
      uploadRow(row, signEndpoint).then(refreshSubmit);
    });
    input.value = "";
  };

  input.addEventListener("change", () => handleFiles(input.files));
  ["dragenter", "dragover"].forEach((evt) =>
    dropzone.addEventListener(evt, (e) => {
      e.preventDefault();
      dropzone.classList.add("ring-2", "ring-brand-500/40");
    }),
  );
  ["dragleave", "drop"].forEach((evt) =>
    dropzone.addEventListener(evt, (e) => {
      e.preventDefault();
      dropzone.classList.remove("ring-2", "ring-brand-500/40");
    }),
  );
  dropzone.addEventListener("drop", (e) => handleFiles(e.dataTransfer?.files));

  submit.addEventListener("click", async (e) => {
    e.preventDefault();
    if (submitting || rows.length === 0) return;
    if (rows.some((r) => !r.ready)) return;
    submitting = true;
    submit.textContent = "Attaching…";
    refreshSubmit();
    setStatus("");

    let current = null;
    try {
      // A retry after a partial failure skips the rows that attached.
      for (const row of rows) {
        if (row.attached) continue;
        current = row;
        clearRowError(row);
        setRowStatus(row, "Attaching…", "text-ink-500");
        await createDocument(createEndpoint, row);
        markAttached(row);
      }
      // Brief success confirmation before the reload so the user sees
      // their action register — the reload itself is the canonical
      // "the new docs are now in the list" feedback, but on a fast
      // connection that swap is too quick to read as success.
      const count = rows.length;
      submit.textContent =
        count === 1 ? "Attached. Reloading…" : `Attached ${count}. Reloading…`;
      window.setTimeout(() => window.location.reload(), 400);
    } catch (err) {
      console.error("academy.attachments create failed", err);
      const message =
        err.userMessage || "Could not attach the file. Please try again.";
      setRowStatus(current, "Not attached", "text-red-600");
      showRowError(current, message);
      submit.textContent = "Attach files";
      submitting = false;
      refreshSubmit();
    }
  });
}

/**
 * @typedef {Object} Row
 * @property {HTMLLIElement} el
 * @property {File} file
 * @property {HTMLInputElement} titleInput
 * @property {HTMLLabelElement} titleLabel
 * @property {HTMLElement} statusEl
 * @property {HTMLButtonElement} removeBtn
 * @property {HTMLParagraphElement | null} reasonEl
 * @property {string} storageKey
 * @property {boolean} ready
 * @property {boolean} attached
 */

/** @returns {Row} */
function createRow(file, onRemove) {
  const el = document.createElement("li");
  el.className =
    "rounded-lg border border-orange-100 bg-white px-3 py-2.5 space-y-2";

  // Header row: filename + upload status pill + remove button.
  const header = document.createElement("div");
  header.className = "flex items-center gap-2";

  const filename = document.createElement("span");
  filename.className = "flex-1 min-w-0 truncate text-xs text-ink-500";
  filename.title = file.name;
  filename.textContent = file.name;

  const statusEl = document.createElement("span");
  statusEl.className =
    "text-[11px] font-medium text-ink-500 shrink-0 transition-colors duration-150 ease-out";
  statusEl.textContent = "Uploading…";

  const removeBtn = document.createElement("button");
  removeBtn.type = "button";
  removeBtn.setAttribute("aria-label", "Remove " + file.name);
  removeBtn.className =
    "inline-grid place-items-center size-6 -mr-1 text-ink-500 hover:text-red-600 shrink-0 text-lg leading-none transition-colors duration-150 ease-out active:scale-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 rounded";
  removeBtn.textContent = "×";
  removeBtn.addEventListener("click", onRemove);

  header.appendChild(filename);
  header.appendChild(statusEl);
  header.appendChild(removeBtn);

  // Title field: labelled, full-width, hard to miss.
  const titleLabel = document.createElement("label");
  titleLabel.className = "block";

  const titleLabelText = document.createElement("span");
  titleLabelText.className =
    "block text-[10px] font-semibold uppercase tracking-wider text-ink-500";
  titleLabelText.textContent = "Display title";

  const titleInput = document.createElement("input");
  titleInput.type = "text";
  titleInput.value = defaultTitle(file.name);
  titleInput.required = true;
  titleInput.maxLength = 120;
  titleInput.placeholder = "How the file is labelled in the attachments list";
  titleInput.className =
    "block w-full px-2.5 py-1.5 mt-1 bg-white border border-orange-200 rounded-md text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-colors disabled:opacity-60";

  titleLabel.appendChild(titleLabelText);
  titleLabel.appendChild(titleInput);

  el.appendChild(header);
  el.appendChild(titleLabel);

  return {
    el,
    file,
    titleInput,
    titleLabel,
    statusEl,
    removeBtn,
    reasonEl: null,
    storageKey: "",
    ready: false,
    attached: false,
  };
}

function defaultTitle(filename) {
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? filename.slice(0, dot) : filename;
}

async function uploadRow(row, signEndpoint) {
  try {
    const signed = await signAndPut(signEndpoint, row.file);
    row.storageKey = signed.key;
    row.ready = true;
    setRowStatus(row, "Ready", "text-emerald-700");
  } catch (err) {
    console.error("academy.attachments upload failed", err);
    row.ready = false;
    setRowStatus(row, "Upload failed", "text-red-600");

    // The file can never attach, so the row drops its title and says
    // why Attach files waits on it.
    row.titleLabel.classList.add("hidden");
    showRowError(
      row,
      err.userMessage
        ? `${err.userMessage} Remove this file to continue.`
        : "Remove the file and try again.",
    );
  }
}

function setRowStatus(row, text, color) {
  row.statusEl.textContent = text;
  row.statusEl.className = `text-[11px] font-medium ${color} shrink-0 transition-colors duration-150 ease-out`;
}

function showRowError(row, message) {
  row.el.classList.replace("border-orange-100", "border-red-200");
  row.el.classList.replace("bg-white", "bg-red-50/40");

  if (!row.reasonEl) {
    row.reasonEl = document.createElement("p");
    row.reasonEl.className = "text-xs text-red-600 -mt-1";
    row.reasonEl.setAttribute("role", "alert");
    row.el.firstElementChild.after(row.reasonEl);
  }
  row.reasonEl.textContent = message;
}

function clearRowError(row) {
  row.el.classList.replace("border-red-200", "border-orange-100");
  row.el.classList.replace("bg-red-50/40", "bg-white");
  row.reasonEl?.remove();
  row.reasonEl = null;
}

// An attached row is on the course, so it locks: the next submit skips
// it, and removing it here would not remove it there.
function markAttached(row) {
  row.attached = true;
  clearRowError(row);
  setRowStatus(row, "Attached", "text-emerald-700");
  row.titleInput.disabled = true;
  row.removeBtn.classList.add("invisible");
}

async function createDocument(endpoint, row) {
  await request(endpoint, {
    fields: {
      title: row.titleInput.value.trim() || row.file.name,
      storage_key: row.storageKey,
    },
  });
}
